import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * RuntimeConfigPage — org-level runtime configuration management.
 *
 * Manages LLM credentials, model defaults, and agent behavior settings.
 * Security and tool lockdown settings are PLATFORM_ADMIN only (not here).
 *
 * §8.10 C5 (2026-07-13): the DYNAMIC credentials table is the SOLE
 * credential editor. The legacy flat-field inputs + the OpenRouter blob
 * form were retired after the fold-forward migration — legacy values now
 * render as READ-ONLY presence status (dormant, shadowed by table entries
 * for the same provider). OAuth stays in its own Connections panel (oauth
 * kind is deferred with the merge).
 *
 * Phase 5 Slice 5.2: the "OAuth Connections" subsection (manual-code
 * flow against `lexserver/src/routes/llm-oauth.ts`). Non-secret structural
 * status is sourced from `useLlmOauth()` (shared `llm-status-view`
 * builders — no tokens, no apiKey, no headers ever cross the wire).
 */
import { AlertTriangle, Check, ExternalLink, Loader2, Save } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLlmOauth } from "../hooks/useLlmOauth.js";
import { useRuntimeConfig } from "../hooks/useRuntimeConfig.js";
import { useOrgAdminClient } from "../hooks/useOrgAdminClient.js";
import { useOrgAdminT } from "../i18n/context.js";
import { ProfilePicker } from "../components/ProfilePicker.js";
import { AliasMatrix, findIncompleteAlias } from "../components/AliasMatrix.js";
import { CredentialsTable, findIncompleteCredential } from "../components/CredentialsTable.js";
import { SearchProvidersTable, findIncompleteSearchCredential, } from "../components/SearchProvidersTable.js";
// Debounce for the AI-Providers page autosave (operator directive
// 2026-07-13): save ~15s after the LAST edit, so an operator who switches
// tabs mid-edit doesn't lose work. Tab-hide + unmount flush immediately.
const AUTOSAVE_DEBOUNCE_MS = 15_000;
// Inlined string literal (intentionally — `@lexiun/lx-llm-auth`'s barrel
// re-exports pi-ai-backed Node modules, importing it from this browser
// bundle would pull `node:crypto`/`fs` into the SPA). Mirrors the
// canonical constant at `lx-llm-auth/src/types.ts` (verified by the
// integration tests that use both surfaces).
const OPENAI_CODEX_PROVIDER_ID = "openai-codex";
const OPENROUTER_PROVIDER_ID = "openrouter";
/** §8.10 C5 — legacy flat fields, rendered as READ-ONLY presence badges
 *  (the dynamic table above is the sole editor; GET masks present values).
 *  The human label is resolved at render via `config.legacyFields.<key>`. */
const LEGACY_LLM_FIELDS = [
    { key: "anthropicOauthToken" },
    { key: "anthropicApiKey" },
    { key: "openaiApiKey" },
    { key: "googleApiKey" },
    { key: "xaiApiKey" },
];
/** Slice 3.D.2: the typed `validation.code` values in the
 *  LLM_VALIDATION_FAILED 422 response — friendly labels live in
 *  `config.validationCodes.<code>` (i18n). */
const VALIDATION_CODES = ["auth", "model-unknown", "quota", "network", "schema", "unknown"];
const THINKING_LEVELS = [
    { value: "low", labelKey: "config.thinkingLow" },
    { value: "medium", labelKey: "config.thinkingMedium" },
    { value: "high", labelKey: "config.thinkingHigh" },
];
function OAuthConnectionPanel({ providerId, displayName, connection, health, onConnect, onComplete, onDisconnect, }) {
    const t = useOrgAdminT();
    // null = idle (showing "Connect" button); { flowId, authUrl } = mid-flow.
    const [flow, setFlow] = useState(null);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    const isConnected = connection?.connected === true;
    // Connected-but-unhealthy: the stored credential is being rejected (or the
    // provider is degraded) — surface it and offer an IN-PLACE Reconnect (the
    // connect flow overwrites the credential; no disconnect needed, so there is
    // never a credential-less window).
    const isBroken = isConnected && (health === "down" || health === "degraded");
    const beginFlow = useCallback(async () => {
        setBusy(true);
        setMsg(null);
        const r = await onConnect(providerId);
        setBusy(false);
        if (!r.ok) {
            setMsg({ ok: false, text: r.error });
            return;
        }
        setFlow({ flowId: r.data.flowId, authUrl: r.data.authUrl });
    }, [onConnect, providerId]);
    const finishFlow = useCallback(async () => {
        if (!flow)
            return;
        if (!code.trim()) {
            setMsg({ ok: false, text: t("oauth.emptyValidation") });
            return;
        }
        setBusy(true);
        setMsg(null);
        const r = await onComplete(providerId, flow.flowId, code.trim());
        setBusy(false);
        if (!r.ok) {
            setMsg({ ok: false, text: r.error });
            return;
        }
        setFlow(null);
        setCode("");
        setMsg({ ok: true, text: t("oauth.connectedMsg") });
        setTimeout(() => setMsg(null), 5000);
    }, [onComplete, providerId, flow, code, t]);
    const cancelFlow = useCallback(() => {
        setFlow(null);
        setCode("");
        setMsg(null);
    }, []);
    const handleDisconnect = useCallback(async () => {
        if (!window.confirm(t("oauth.disconnectConfirm", { name: displayName }))) {
            return;
        }
        setBusy(true);
        setMsg(null);
        const r = await onDisconnect(providerId);
        setBusy(false);
        if (!r.ok) {
            setMsg({ ok: false, text: r.error });
            return;
        }
        setMsg({ ok: true, text: t("oauth.disconnectedMsg") });
        setTimeout(() => setMsg(null), 5000);
    }, [onDisconnect, providerId, displayName, t]);
    return (_jsxs("div", { className: "space-y-2", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "w-48 shrink-0 text-sm font-medium text-slate-700 dark:text-slate-300", children: displayName }), _jsx("div", { className: "flex flex-1 items-center gap-2", children: isConnected ? (_jsxs(_Fragment, { children: [isBroken ? (_jsxs("span", { "data-testid": `oauth-broken-${providerId}`, className: health === "down"
                                        ? "inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950 dark:text-red-300"
                                        : "inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-950 dark:text-amber-300", children: [_jsx("span", { className: health === "down" ? "h-1.5 w-1.5 rounded-full bg-red-500" : "h-1.5 w-1.5 rounded-full bg-amber-500" }), health === "down" ? t("oauth.connectionBroken") : t("oauth.degraded")] })) : (_jsxs("span", { className: "inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-950 dark:text-green-300", children: [_jsx("span", { className: "h-1.5 w-1.5 rounded-full bg-green-500" }), t("oauth.connected")] })), connection?.accountId && (_jsx("span", { className: "text-xs text-slate-500 dark:text-slate-400", children: connection.accountId })), connection?.planType && (_jsx("span", { className: "rounded bg-slate-100 px-1.5 py-0.5 text-xs uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300", children: connection.planType })), isBroken && !flow && (_jsx("button", { type: "button", "data-testid": `oauth-reconnect-${providerId}`, onClick: beginFlow, disabled: busy, className: "rounded-md border border-red-300 bg-red-50 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50 dark:border-red-700 dark:bg-red-950 dark:text-red-300 dark:hover:bg-red-900", children: t("oauth.reconnect") })), isBroken && flow && (_jsx("span", { className: "text-xs text-slate-500 dark:text-slate-400", children: t("oauth.awaitingCode") })), _jsx("button", { type: "button", onClick: handleDisconnect, disabled: busy, className: "ml-auto text-xs text-red-500 hover:underline disabled:opacity-50", children: t("common.disconnect") })] })) : flow ? (_jsx("span", { className: "text-xs text-slate-500 dark:text-slate-400", children: t("oauth.awaitingCode") })) : (_jsx("button", { type: "button", onClick: beginFlow, disabled: busy, className: "rounded-md border border-indigo-300 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 dark:border-indigo-700 dark:bg-indigo-950 dark:text-indigo-300 dark:hover:bg-indigo-900", children: t("common.connect") })) })] }), flow && (_jsxs("div", { className: "ml-48 space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs dark:border-slate-700 dark:bg-slate-900", children: [_jsx("p", { className: "text-slate-700 dark:text-slate-300", children: t("oauth.step1", { name: displayName }) }), _jsxs("a", { href: flow.authUrl, target: "_blank", rel: "noopener noreferrer", className: "inline-flex items-center gap-1 break-all text-indigo-600 hover:underline dark:text-indigo-400", children: [flow.authUrl, _jsx(ExternalLink, { className: "h-3 w-3 shrink-0" })] }), _jsx("p", { className: "text-slate-700 dark:text-slate-300", children: t("oauth.step2") }), _jsx("p", { className: "text-slate-700 dark:text-slate-300", children: t("oauth.step3") }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("input", { type: "text", value: code, onChange: (e) => setCode(e.target.value), placeholder: t("oauth.urlPlaceholder"), autoComplete: "off", spellCheck: false, className: "flex-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 font-mono text-xs dark:border-slate-600 dark:bg-slate-800" }), _jsx("button", { type: "button", onClick: finishFlow, disabled: busy, className: "rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50", children: busy ? t("oauth.connecting") : t("oauth.complete") }), _jsx("button", { type: "button", onClick: cancelFlow, disabled: busy, className: "text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300", children: t("common.cancel") })] })] })), msg && (_jsx("p", { className: `ml-48 text-xs ${msg.ok ? "text-green-600 dark:text-green-400" : "text-red-500"}`, children: msg.text }))] }));
}
export function RuntimeConfigPage() {
    const t = useOrgAdminT();
    const { config, loading, error, save, reload } = useRuntimeConfig();
    const client = useOrgAdminClient();
    const { status: oauthStatus, loading: oauthLoading, reload: oauthReload, init: oauthInit, complete: oauthComplete, disconnect: oauthDisconnect, } = useLlmOauth();
    // Local form state
    const [models, setModels] = useState({
        defaultModel: "",
        // §P4.A of llm-resolver-unification-impl-plan.md — composite
        // resolver-unified profile name ("MED" or "MED:high"). Coexists
        // with defaultModel during the additive window; §P10 close-out
        // drops the legacy ref + tightens this to required.
        chatProfile: "",
        maxBudgetPerTaskUsd: "",
    });
    const [agent, setAgent] = useState({
        defaultThinkingLevel: "",
        maxTurns: "",
        systemPromptPrefix: "",
    });
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState(null);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    // P3 §8.9 — the per-org DYNAMIC ALIASES table + its live per-alias effective
    // rung (from GET /runtime-config/llm-status). Edited via <AliasMatrix>; saved
    // as a whole-table replace.
    const [aliases, setAliases] = useState({});
    // §8.10 (R-17) — the dynamic credentials table (masked secrets from GET).
    const [credentials, setCredentials] = useState({});
    // S5 — the SEARCH credential table, wired exactly like its LLM sibling above
    // (same load → dirty-check → PATCH shape) so the two cannot drift.
    const [searchCredentials, setSearchCredentials] = useState({});
    const [effectiveTiers, setEffectiveTiers] = useState(undefined);
    // Live per-provider health (P4 UX gap) — drives the OAuth panel's
    // Connection-broken pill + Reconnect affordance.
    const [providerHealth, setProviderHealth] = useState(undefined);
    // Server-side preview of the chatProfile resolution — refetched on
    // every picker change so the operator sees what `MED:high` actually
    // resolves to BEFORE clicking Save.
    const [profilePreview, setProfilePreview] = useState({
        loading: false,
        resolved: null,
        error: null,
    });
    useEffect(() => {
        const name = models.chatProfile.trim();
        if (!name) {
            setProfilePreview({ loading: false, resolved: null, error: null });
            return;
        }
        // Debounce — picker changes can fire two events in quick succession
        // (base + thinking dropdowns). 150ms is below human-perceptible.
        let cancelled = false;
        setProfilePreview((p) => ({ ...p, loading: true }));
        const timer = setTimeout(async () => {
            try {
                const res = await client.resolveChatProfile(name);
                if (cancelled)
                    return;
                setProfilePreview({
                    loading: false,
                    resolved: res.data.resolved,
                    error: res.data.error,
                });
            }
            catch (err) {
                if (cancelled)
                    return;
                setProfilePreview({
                    loading: false,
                    resolved: null,
                    error: {
                        code: "REQUEST_FAILED",
                        message: err instanceof Error ? err.message : t("config.previewRequestFailed"),
                    },
                });
            }
        }, 150);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [models.chatProfile, client, t]);
    // Populate form from loaded config
    useEffect(() => {
        if (!config)
            return;
        setModels({
            defaultModel: config.models?.defaultModel ?? "",
            chatProfile: config.models?.chatProfile ?? "",
            maxBudgetPerTaskUsd: config.models?.maxBudgetPerTaskUsd?.toString() ?? "",
        });
        setAgent({
            defaultThinkingLevel: config.agent?.defaultThinkingLevel ?? "",
            maxTurns: config.agent?.maxTurns?.toString() ?? "",
            systemPromptPrefix: config.agent?.systemPromptPrefix ?? "",
        });
        setAliases(config.models?.llmProfiles ?? {});
        setCredentials(config.llm?.credentials ?? {});
        setSearchCredentials(config.search?.credentials ?? {});
    }, [config]);
    // Fetch the live per-alias effective-rung diagnostic. Reloadable so a save
    // re-resolves the table (the parent's `save()` only refreshes `config`).
    const loadLlmStatus = useCallback(async () => {
        try {
            const res = await client.getLlmStatus();
            setEffectiveTiers(res.data.effectiveTiers);
            setProviderHealth(res.data.providerHealth);
        }
        catch {
            setEffectiveTiers(undefined); // non-fatal — the matrix still edits
            setProviderHealth(undefined);
        }
    }, [client]);
    useEffect(() => {
        void loadLlmStatus();
    }, [loadLlmStatus]);
    // S5 — the search-provider status feeding the badge + the provider offer list.
    //
    // ⚠ `undefined` is LOAD-FAILED **and** NOT-YET-LOADED, deliberately: the badge
    // renders NOTHING for either, which is a decline to claim rather than a claim
    // of "not configured". Collapsing a failed read into a value is the exact
    // shape `searchProviderBadge` exists to prevent, so the failure path sets
    // `undefined` and says nothing. Non-fatal — the table still edits.
    const [searchStatus, setSearchStatus] = useState();
    // ⚠ MONOTONIC TOKEN — this fetch has TWO callers (mount, and every save that
    // touches search), so responses can land out of order. The dangerous
    // interleaving is specifically: the mount request resolves AFTER the
    // post-save refetch and overwrites fresh status with PRE-SAVE status —
    // reinstating the exact stale badge the re-resolve below exists to prevent.
    // Same guard this lane needed for the git-diff viewer (`07eda3c08`) and the
    // client delete-census (`96ee915b5`).
    const searchStatusReq = useRef(0);
    const loadSearchStatus = useCallback(async () => {
        const token = ++searchStatusReq.current;
        try {
            const res = await client.getSearchStatus();
            if (token !== searchStatusReq.current)
                return; // superseded — drop it
            setSearchStatus(res.data.providers);
        }
        catch {
            if (token !== searchStatusReq.current)
                return;
            setSearchStatus(undefined);
        }
    }, [client]);
    useEffect(() => {
        void loadSearchStatus();
    }, [loadSearchStatus]);
    // Providers the org holds a usable credential for — an affordance for the
    // matrix pickers (the server's validate-then-save is the hard gate). Derived
    // from the masked flat fields + the OAuth/custom status view + the §8.10
    // DYNAMIC credentials table (the merged view, per the plan). The table is
    // read from LIVE edit state: an unsaved row lights its provider immediately,
    // and the server gate accepts a one-Save "credential + alias" bundle.
    const credentialedProviders = useMemo(() => {
        const s = new Set();
        if (config?.llm?.anthropicApiKey || config?.llm?.anthropicOauthToken)
            s.add("anthropic");
        if (config?.llm?.openaiApiKey)
            s.add("openai");
        if (config?.llm?.googleApiKey)
            s.add("google");
        if (config?.llm?.xaiApiKey)
            s.add("xai");
        if (oauthStatus?.oauthConnections?.[OPENAI_CODEX_PROVIDER_ID]?.connected)
            s.add("openai-codex");
        if (oauthStatus?.customProviders?.[OPENROUTER_PROVIDER_ID])
            s.add("openrouter");
        for (const e of Object.values(credentials)) {
            if (e.kind !== "oauth" && e.secret?.trim())
                s.add(e.provider);
        }
        return [...s];
    }, [config, oauthStatus, credentials]);
    // Diff the live form against the loaded config ONCE — drives `isDirty`
    // (the floating save button + autosave + unsaved-changes guards) AND the
    // save itself, so the two can never disagree. `blocking` is the first
    // incomplete-row message among CHANGED sections (a half-filled credential
    // or alias the atomic PATCH would 400 on); `patch` carries only the
    // complete, changed sections.
    const { patch, isDirty, blocking } = useMemo(() => {
        const patch = {};
        let dirty = false;
        let blocking = null;
        // §8.10 C5 — the dynamic credentials table is the SOLE credential editor.
        const llmPatch = {};
        const currentCredentials = config?.llm?.credentials ?? {};
        if (JSON.stringify(credentials) !== JSON.stringify(currentCredentials)) {
            dirty = true;
            const inc = findIncompleteCredential(credentials, t);
            if (inc)
                blocking ??= t("config.blockingCredential", { detail: inc });
            else
                llmPatch.credentials = credentials;
        }
        if (Object.keys(llmPatch).length > 0)
            patch.llm = llmPatch;
        // S5 — same dirty-check shape as the LLM table. `search.credentials` is
        // wholesale-replaced on PATCH, and the masked sentinel round-trips under an
        // EXISTING id (the table clears it on rename, where the server would reject).
        const currentSearch = config?.search?.credentials ?? {};
        if (JSON.stringify(searchCredentials) !== JSON.stringify(currentSearch)) {
            dirty = true;
            // ⚠ BLOCK an incomplete row HERE, exactly as the LLM table does. The
            // PATCH is ATOMIC: sending an empty secret 400s the whole request and
            // takes any bundled LLM/alias edits down with it, while the operator sees
            // only a server error about a field they were still filling in.
            const inc = findIncompleteSearchCredential(searchCredentials, t);
            if (inc)
                blocking ??= t("config.blockingCredential", { detail: inc });
            else {
                patch.search = { ...(config?.search ?? {}), credentials: searchCredentials };
            }
        }
        const modelsPatch = {};
        if (models.defaultModel !== (config?.models?.defaultModel ?? "")) {
            dirty = true;
            modelsPatch.defaultModel = models.defaultModel || undefined;
        }
        if (models.chatProfile !== (config?.models?.chatProfile ?? "")) {
            dirty = true;
            modelsPatch.chatProfile = models.chatProfile || undefined;
        }
        const budgetNum = models.maxBudgetPerTaskUsd ? Number.parseFloat(models.maxBudgetPerTaskUsd) : undefined;
        if (budgetNum !== config?.models?.maxBudgetPerTaskUsd) {
            dirty = true;
            modelsPatch.maxBudgetPerTaskUsd = budgetNum;
        }
        const currentAliases = config?.models?.llmProfiles ?? {};
        if (JSON.stringify(aliases) !== JSON.stringify(currentAliases)) {
            dirty = true;
            const inc = findIncompleteAlias(aliases, t);
            if (inc)
                blocking ??= t("config.blockingAlias", { detail: inc });
            else
                modelsPatch.llmProfiles = aliases;
        }
        if (Object.keys(modelsPatch).length > 0)
            patch.models = modelsPatch;
        const agentPatch = {};
        if (agent.defaultThinkingLevel !== (config?.agent?.defaultThinkingLevel ?? "")) {
            dirty = true;
            agentPatch.defaultThinkingLevel = agent.defaultThinkingLevel || undefined;
        }
        const turnsNum = agent.maxTurns ? Number.parseInt(agent.maxTurns, 10) : undefined;
        if (turnsNum !== config?.agent?.maxTurns) {
            dirty = true;
            agentPatch.maxTurns = turnsNum;
        }
        if (agent.systemPromptPrefix !== (config?.agent?.systemPromptPrefix ?? "")) {
            dirty = true;
            agentPatch.systemPromptPrefix = agent.systemPromptPrefix || undefined;
        }
        if (Object.keys(agentPatch).length > 0)
            patch.agent = agentPatch;
        return { patch, isDirty: dirty, blocking };
        // ⚠ EVERY piece of edit state this body READS must be listed here, or the
        // memo serves a stale verdict and that section becomes UNSAVEABLE — the
        // save dock stays "no changes" while the user is looking at their change.
        // `searchCredentials` was read at the S5 block above and missing here,
        // which made search-only edits impossible to save (found by `ide` in a LIVE
        // SP4 verify, 2026-08-15). Unit renders cannot catch this: they build the
        // memo fresh every time, so only a real editing session exercises staleness.
    }, [models, agent, config, aliases, credentials, searchCredentials, t]);
    // Signature of the exact edit an AUTOSAVE last failed on (server-side, e.g.
    // a real-LLM validation reject). Autosave must NOT re-hammer the same
    // failing PATCH every 15s (server load + a re-toasted error) — it stays
    // suppressed until the operator edits (new signature) or saves manually.
    // Declared before `handleSave` (which mutates it); the autosave effect reads it.
    const autoSaveFailedSigRef = useRef(null);
    // `auto` = an autosave (debounce / tab-hide / unmount): stays SILENT on a
    // no-op or a blocking incomplete row (no toast spam), where a manual Save
    // surfaces the reason. Returns whether a PATCH was actually sent.
    const handleSave = useCallback(async (opts) => {
        const auto = opts?.auto ?? false;
        if (blocking) {
            if (!auto)
                setSaveMsg({ ok: false, text: blocking });
            return false;
        }
        if (Object.keys(patch).length === 0) {
            if (!auto)
                setSaveMsg({ ok: true, text: t("config.noChangesToSave") });
            return false;
        }
        setSaving(true);
        setSaveMsg(null);
        const llmPatch = (patch.llm ?? {});
        const modelsPatch = (patch.models ?? {});
        const result = await save(patch);
        if (result.ok) {
            // Refresh the OAuth/custom-provider status view when the credentials
            // table changed — a saved openrouter/custom entry flips the merged
            // "Configured" status (oauthStatus loads independently of `config`).
            if (llmPatch.credentials !== undefined) {
                void oauthReload();
            }
            // Re-resolve the alias table so the per-alias "resolves" badge reflects the
            // just-saved rows (save() only refreshes `config`, not llm-status).
            if (modelsPatch.llmProfiles !== undefined || llmPatch.credentials !== undefined) {
                void loadLlmStatus();
            }
            // 🔴 ACCEPTANCE CRITERION, not bookkeeping (round 7). A saved org key must
            // flip the badge off `platform-default`. `save()` refreshes `config`, NOT
            // the status view — so without this the operator saves a key, the badge
            // does not move, and a stale read reads as "the save failed" on the one
            // surface that exists to say where their key comes from.
            if (patch.search !== undefined) {
                void loadSearchStatus();
            }
            autoSaveFailedSigRef.current = null; // a good save clears any prior auto-fail lock
            setLastSavedAt(Date.now());
            setSaveMsg({
                ok: true,
                text: auto ? t("config.autoSaved") : t("config.configSaved"),
            });
            setSaving(false);
            setTimeout(() => setSaveMsg(null), 5000);
            return true;
        }
        // Slice 3.D.2: render the typed LLM_VALIDATION_FAILED 422 payload
        // when a defaultModel change triggers the lexserver real-LLM ping
        // and the ping fails. Falls back to the bare error string for any
        // other failure code.
        if (result.code === "LLM_VALIDATION_FAILED") {
            const v = result.details?.validation ?? {};
            const code = v.code ?? "unknown";
            const label = VALIDATION_CODES.includes(code)
                ? t(`config.validationCodes.${code}`)
                : code;
            const detail = v.message ? ` — ${v.message}` : "";
            const latency = typeof v.latencyMs === "number" ? ` (${v.latencyMs}ms)` : "";
            setSaveMsg({
                ok: false,
                text: t("config.validationFailed", { label, detail, latency }),
            });
        }
        else if (result.code === "MISSING_CREDENTIAL_FOR_PROVIDER") {
            // Org-credential gate: the operator picked a provider that the org
            // doesn't have a credential for. The error message from the server
            // is operator-actionable.
            setSaveMsg({ ok: false, text: result.error });
        }
        else {
            setSaveMsg({ ok: false, text: result.error });
        }
        // Suppress autosave for THIS exact edit until the operator changes it or
        // retries manually (see autoSaveFailedSigRef) — no 15s retry storm.
        if (auto)
            autoSaveFailedSigRef.current = JSON.stringify(patch);
        setSaving(false);
        return false;
        // `loadSearchStatus` is listed for the same reason `searchCredentials` had
        // to be added to the dirty memo an hour ago: this body CALLS it, so a stale
        // closure here would silently stop re-resolving the badge after a save.
    }, [patch, blocking, save, oauthReload, loadLlmStatus, loadSearchStatus, t]);
    // ── Autosave + unsaved-changes guards (operator directive 2026-07-13) ──
    // Ref-mirror the latest save + dirty state so the window/unmount handlers
    // below fire the CURRENT save without re-subscribing every keystroke.
    const saveRef = useRef(handleSave);
    const dirtyRef = useRef(isDirty);
    const blockingRef = useRef(blocking);
    useEffect(() => {
        saveRef.current = handleSave;
        dirtyRef.current = isDirty;
        blockingRef.current = blocking;
    });
    const patchSig = useMemo(() => JSON.stringify(patch), [patch]);
    // 1. Debounced autosave — ~15s after the LAST edit (the `patch`-keyed
    //    effect resets the timer on every change), only when dirty, complete,
    //    not already saving, and not a known-failing edit.
    useEffect(() => {
        if (!isDirty || blocking || saving)
            return;
        if (autoSaveFailedSigRef.current === patchSig)
            return;
        const t = setTimeout(() => void handleSave({ auto: true }), AUTOSAVE_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [isDirty, blocking, saving, handleSave, patchSig]);
    // 2. Save on tab-hide — the exact "switch tabs and forget" case: flush
    //    immediately when the page is hidden (browser tab switch / minimize).
    useEffect(() => {
        const onHide = () => {
            if (document.visibilityState === "hidden" && dirtyRef.current && !blockingRef.current) {
                void saveRef.current({ auto: true });
            }
        };
        document.addEventListener("visibilitychange", onHide);
        return () => document.removeEventListener("visibilitychange", onHide);
    }, []);
    // 3. beforeunload — native "leave site?" prompt on close/reload/external
    //    nav while unsaved (the hard block for changes autosave hasn't caught).
    useEffect(() => {
        if (!isDirty)
            return;
        const onBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [isDirty]);
    // 4. Flush on unmount — clicking another org-admin tab (Users/Settings/…)
    //    unmounts this page inside a BrowserRouter (no useBlocker available),
    //    so best-effort auto-save the pending changes on the way out. Runs once
    //    at unmount via the refs (no dep churn).
    useEffect(() => {
        return () => {
            if (dirtyRef.current && !blockingRef.current) {
                void saveRef.current({ auto: true });
            }
        };
    }, []);
    // OpenRouter "Disconnect" — clears the encrypted providersJson blob in
    // a one-shot PATCH (no other field touched). Separate from `handleSave`
    // so the operator doesn't have to scroll past unsaved changes elsewhere.
    const disconnectOpenrouter = useCallback(async () => {
        if (!window.confirm(t("config.openrouterDisconnectConfirm"))) {
            return;
        }
        setSaving(true);
        setSaveMsg(null);
        const result = await save({ llm: { providersJson: "" } });
        if (result.ok) {
            // Refresh the status view so the "Configured" badge disappears
            // immediately (oauthStatus is loaded independently of `config`).
            void oauthReload();
        }
        setSaveMsg(result.ok
            ? { ok: true, text: t("config.openrouterDisconnected") }
            : { ok: false, text: result.error });
        setSaving(false);
        if (result.ok)
            setTimeout(() => setSaveMsg(null), 5000);
    }, [save, oauthReload, t]);
    if (loading) {
        return _jsx("p", { className: "py-12 text-center text-sm text-slate-400", children: t("config.loading") });
    }
    if (error && !config) {
        return (_jsxs("div", { className: "py-12 text-center", children: [_jsx("p", { className: "text-sm text-red-500", children: error }), _jsx("button", { type: "button", onClick: reload, className: "mt-2 text-xs text-indigo-500 hover:underline", children: t("common.retry") })] }));
    }
    return (
    // No max-width — the credentials + alias editors are wide tables; capping
    // the pane forced a horizontal scrollbar (operator report 2026-07-13).
    _jsxs("div", { className: "space-y-8", children: [_jsxs("section", { children: [_jsx("h3", { className: "mb-3 text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400", children: t("config.sectionCredentials") }), _jsx("div", { className: "mb-4 space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-700", children: _jsx(CredentialsTable, { value: credentials, onChange: setCredentials, disabled: saving }) }), _jsx("div", { className: "mb-4 space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-700", children: _jsx(SearchProvidersTable, { credentials: searchCredentials, onChange: setSearchCredentials, status: searchStatus, enabled: config?.search?.enabled ?? true, disabled: saving }) }), _jsx("h3", { className: "mb-3 text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400", children: t("config.sectionLegacy") }), _jsx("div", { className: "space-y-2 rounded-lg border border-slate-200 p-4 dark:border-slate-700", children: LEGACY_LLM_FIELDS.some(({ key }) => config?.llm?.[key]) ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "flex flex-wrap gap-1.5", "data-testid": "legacy-llm-presence", children: LEGACY_LLM_FIELDS.filter(({ key }) => config?.llm?.[key]).map(({ key }) => (_jsxs("span", { className: "inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300", children: [_jsx("span", { className: "h-1.5 w-1.5 rounded-full bg-slate-400" }), t(`config.legacyFields.${key}`)] }, key))) }), _jsx("p", { className: "text-xs text-slate-400 dark:text-slate-500", children: t("config.legacyPresenceHelp") })] })) : (_jsx("p", { className: "text-xs italic text-slate-400", children: t("config.legacyNone") })) })] }), _jsxs("section", { children: [_jsx("h3", { className: "mb-3 text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400", children: t("config.sectionOauth") }), _jsxs("div", { className: "space-y-4 rounded-lg border border-slate-200 p-4 dark:border-slate-700", children: [oauthLoading ? (_jsx("p", { className: "text-sm text-slate-400", children: t("config.oauthLoading") })) : (_jsx(OAuthConnectionPanel, { providerId: OPENAI_CODEX_PROVIDER_ID, displayName: t("oauth.openaiCodexDisplayName"), connection: oauthStatus?.oauthConnections?.[OPENAI_CODEX_PROVIDER_ID], health: providerHealth?.[OPENAI_CODEX_PROVIDER_ID]?.status, onConnect: oauthInit, onComplete: async (provider, flowId, code) => {
                                    const r = await oauthComplete(provider, flowId, code);
                                    // A successful complete = the P2 verification probe — the
                                    // server clears providerHealth; refresh so the broken pill
                                    // flips back without a reload.
                                    if (r.ok)
                                        void loadLlmStatus();
                                    return r;
                                }, onDisconnect: oauthDisconnect })), _jsx("p", { className: "text-xs text-slate-400", children: t("config.oauthHelp") })] })] }), _jsxs("section", { children: [_jsx("h3", { className: "mb-3 text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400", children: t("config.sectionOpenrouter") }), _jsxs("div", { className: "space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-700", children: [oauthLoading ? (_jsx("p", { className: "text-sm text-slate-400", children: t("config.openrouterLoading") })) : oauthStatus?.customProviders?.[OPENROUTER_PROVIDER_ID] ? (_jsxs("div", { className: "flex items-center gap-2 text-sm", children: [_jsxs("span", { className: "inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-950 dark:text-green-300", children: [_jsx("span", { className: "h-1.5 w-1.5 rounded-full bg-green-500" }), t("config.configured")] }), _jsxs("span", { className: "text-xs text-slate-500 dark:text-slate-400", children: [oauthStatus.customProviders[OPENROUTER_PROVIDER_ID].baseUrl, " \u2014", " ", oauthStatus.customProviders[OPENROUTER_PROVIDER_ID].models.join(", ")] }), _jsx("button", { type: "button", onClick: disconnectOpenrouter, disabled: saving, className: "ml-auto text-xs text-red-500 hover:underline disabled:opacity-50", children: t("common.disconnect") })] })) : (_jsx("p", { className: "text-sm text-slate-500 dark:text-slate-400", children: t("config.notConfigured") })), _jsx("p", { className: "text-xs text-slate-400", children: t("config.openrouterHelp") })] })] }), _jsxs("section", { children: [_jsx("h3", { className: "mb-3 text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400", children: t("config.sectionModelDefaults") }), _jsxs("div", { className: "space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-700", children: [_jsxs("div", { className: "flex items-start gap-2", children: [_jsx("label", { className: "w-48 shrink-0 pt-1 text-sm font-medium text-slate-700 dark:text-slate-300", children: t("config.chatProfile") }), _jsxs("div", { className: "flex-1", children: [_jsx(ProfilePicker, { value: models.chatProfile, onChange: (v) => setModels((p) => ({ ...p, chatProfile: v })), disabled: saving }), _jsxs("div", { className: "mt-1 text-xs", children: [profilePreview.loading && (_jsx("span", { className: "text-slate-400", children: t("config.resolving") })), !profilePreview.loading && profilePreview.resolved && (_jsxs("div", { className: "rounded-md bg-emerald-50 px-2 py-1 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300", children: [t("config.resolvesTo"), " ", _jsxs("code", { className: "font-mono", children: [profilePreview.resolved.provider, "/", profilePreview.resolved.model] }), profilePreview.resolved.thinkingDefault && (_jsxs(_Fragment, { children: [" ", t("config.resolvesThinking"), " ", _jsx("code", { className: "font-mono", children: profilePreview.resolved.thinkingDefault })] })), profilePreview.resolved.maxOutputTokens !== null && (_jsxs(_Fragment, { children: [" ", t("config.resolvesMaxOut"), _jsx("code", { className: "font-mono", children: profilePreview.resolved.maxOutputTokens })] }))] })), !profilePreview.loading && profilePreview.error && (_jsxs("div", { className: "rounded-md bg-amber-50 px-2 py-1 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300", children: [profilePreview.error.code, ": ", profilePreview.error.message] })), !profilePreview.loading &&
                                                        !profilePreview.resolved &&
                                                        !profilePreview.error && (_jsxs("span", { className: "text-slate-500", children: [t("config.profileHelpA"), " ", _jsx("code", { children: "llmProfiles" }), " ", t("config.profileHelpB"), " ", _jsx("code", { children: "<BASE>:<thinking>" }), " ", t("config.profileHelpC")] }))] })] })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("label", { className: "w-48 shrink-0 text-sm font-medium text-slate-700 dark:text-slate-300", children: t("config.budgetPerTask") }), _jsxs("div", { className: "flex flex-1 items-center gap-1", children: [_jsx("span", { className: "text-sm text-slate-500", children: "$" }), _jsx("input", { type: "number", value: models.maxBudgetPerTaskUsd, onChange: (e) => setModels((p) => ({ ...p, maxBudgetPerTaskUsd: e.target.value })), placeholder: "2.00", min: "0", max: "100", step: "0.01", className: "flex-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-800" })] })] })] })] }), _jsxs("section", { children: [_jsx("h3", { className: "mb-3 text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400", children: t("config.sectionAliases") }), _jsxs("div", { className: "space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-700", children: [_jsxs("p", { className: "text-xs text-slate-500 dark:text-slate-400", children: [t("config.aliasesHelpBeforePrimary"), " ", _jsx("span", { className: "font-medium", children: t("config.aliasesHelpPrimary") }), " ", t("config.aliasesHelpTo"), " ", _jsx("span", { className: "font-medium", children: t("config.aliasesHelpFallback") }), " ", t("config.aliasesHelpAfter")] }), _jsx(AliasMatrix, { value: aliases, onChange: setAliases, effectiveTiers: effectiveTiers, credentialedProviders: credentialedProviders, disabled: saving })] })] }), _jsxs("section", { children: [_jsx("h3", { className: "mb-3 text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400", children: t("config.sectionAgent") }), _jsxs("div", { className: "space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-700", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("label", { className: "w-48 shrink-0 text-sm font-medium text-slate-700 dark:text-slate-300", children: t("config.thinkingLevel") }), _jsxs("select", { value: agent.defaultThinkingLevel, onChange: (e) => setAgent((p) => ({ ...p, defaultThinkingLevel: e.target.value })), className: "flex-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-800", children: [_jsx("option", { value: "", children: t("config.notSet") }), THINKING_LEVELS.map((l) => (_jsx("option", { value: l.value, children: t(l.labelKey) }, l.value)))] })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("label", { className: "w-48 shrink-0 text-sm font-medium text-slate-700 dark:text-slate-300", children: t("config.maxAgentTurns") }), _jsx("input", { type: "number", value: agent.maxTurns, onChange: (e) => setAgent((p) => ({ ...p, maxTurns: e.target.value })), placeholder: "20", min: "1", max: "100", className: "flex-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-800" })] }), _jsxs("div", { className: "flex gap-2", children: [_jsx("label", { className: "w-48 shrink-0 pt-1.5 text-sm font-medium text-slate-700 dark:text-slate-300", children: t("config.systemPromptPrefix") }), _jsx("textarea", { value: agent.systemPromptPrefix, onChange: (e) => setAgent((p) => ({ ...p, systemPromptPrefix: e.target.value })), placeholder: t("config.systemPromptPlaceholder"), maxLength: 2000, rows: 3, className: "flex-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-800" })] }), _jsx("p", { className: "text-xs text-slate-400", children: t("config.systemPromptHelp") })] })] }), _jsx("div", { className: "border-t border-slate-200 pt-4 pb-24 text-xs text-slate-400 dark:border-slate-700", children: config?.updatedAt && (_jsxs("span", { children: [t("config.lastUpdated", { date: new Date(config.updatedAt).toLocaleString() }), config.updatedBy ? t("config.lastUpdatedBy", { user: config.updatedBy }) : ""] })) }), _jsx(SaveDock, { saving: saving, isDirty: isDirty, blocking: blocking, saveMsg: saveMsg, lastSavedAt: lastSavedAt, onSave: () => void handleSave() })] }));
}
// ── Floating save dock ───────────────────────────────────────────
//
// Fixed bottom-right. Four visual states: saving · unsaved-blocked (a
// half-filled row) · unsaved-ready · clean/saved. Autosave handles most
// saves; the button is the manual escape hatch + the always-visible status.
function SaveDock({ saving, isDirty, blocking, saveMsg, lastSavedAt, onSave, }) {
    const t = useOrgAdminT();
    const status = saving
        ? {
            icon: _jsx(Loader2, { className: "h-4 w-4 animate-spin" }),
            label: t("saveDock.saving"),
            cls: "bg-indigo-600 text-white",
        }
        : isDirty && blocking
            ? {
                icon: _jsx(AlertTriangle, { className: "h-4 w-4" }),
                label: t("saveDock.fixToSave"),
                cls: "bg-amber-500 text-white hover:bg-amber-600",
                hint: blocking,
            }
            : isDirty
                ? {
                    icon: _jsx(Save, { className: "h-4 w-4" }),
                    label: t("saveDock.saveChanges"),
                    cls: "bg-indigo-600 text-white hover:bg-indigo-700",
                    hint: t("saveDock.unsavedHint"),
                }
                : {
                    icon: _jsx(Check, { className: "h-4 w-4" }),
                    label: lastSavedAt ? t("saveDock.saved") : t("saveDock.noChanges"),
                    cls: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 cursor-default",
                };
    const showHint = saveMsg?.text ?? status.hint;
    return (_jsxs("div", { className: "fixed bottom-5 right-5 z-40 flex flex-col items-end gap-1.5", "data-testid": "ai-providers-save-dock", children: [showHint && (_jsx("span", { className: `max-w-xs rounded-md px-2.5 py-1 text-xs shadow-sm ${saveMsg && !saveMsg.ok
                    ? "bg-red-50 text-red-600 dark:bg-red-950/50 dark:text-red-300"
                    : saveMsg?.ok
                        ? "bg-green-50 text-green-700 dark:bg-green-950/50 dark:text-green-300"
                        : "bg-slate-800 text-slate-100 dark:bg-slate-200 dark:text-slate-800"}`, children: showHint })), _jsxs("button", { type: "button", onClick: onSave, disabled: saving || (!isDirty && !blocking), "aria-label": t("saveDock.ariaSave"), className: `inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold shadow-lg transition-colors disabled:cursor-default disabled:opacity-90 ${status.cls}`, children: [status.icon, status.label] })] }));
}
//# sourceMappingURL=RuntimeConfigPage.js.map